/**
 * TeamConnectionsSection: the connections a team is read through, set by
 * staff. The GitHub App installation (TeamGithubConnection) signs every
 * read of the team's repositories: one per team, tied to the App's
 * install status, so it has its own section.
 *
 * The S3 connections a team reads its buckets through (TRA-922). Batches
 * and benchmark ingestion both gate on this list, and the customer never
 * creates one: they only pick from what is assigned here.
 *
 * Two S3 kinds. An assumed role stores nothing secret: the customer creates a
 * role named trainloop-* in their account and trusts TrainLoop's connector
 * on it, gated on an External ID. That id is the connection's own id, so
 * it exists only once the row does. The row therefore shows the External
 * ID and the connector principal with copy buttons, and staff hand both to
 * the customer to finish the trust policy. A key pair is the fallback: the
 * secret crosses the wire once, is encrypted before the row is written,
 * and is never shown again.
 */

import { useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { getApiErrorDetail } from '@/api/client'
import { getListConnectionsApiConnectionsGetQueryKey } from '@/api/generated/connections/connections'
import {
  getListTeamConnectionsApiInternalTeamsTeamIdConnectionsGetQueryKey,
  useCreateTeamConnectionApiInternalTeamsTeamIdConnectionsPost,
  useDeleteTeamConnectionApiInternalConnectionsConnectionIdDelete,
  useListTeamConnectionsApiInternalTeamsTeamIdConnectionsGet,
} from '@/api/generated/internal/internal'
import type { ConnectionAdminItem, ImpersonatableTeam } from '@/api/generated/model'
import { RowAction } from '@/components/settings/RowAction'
import { SettingsFormFooter } from '@/components/settings/SettingsFormFooter'
import { SettingsList, SettingsListRow } from '@/components/settings/SettingsList'
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader'
import { SettingsBlock, SettingsSection } from '@/components/settings/SettingsSection'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import { EYEBROW } from '@/components/ui/eyebrow'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'

import {
  S3_CONNECTION_KINDS,
  S3_CONNECTION_KIND_LABELS,
  emptyS3ConnectionForm,
  isS3ConnectionKind,
  s3ConnectionRequest,
  s3ConnectionsOf,
  validateS3Connection,
  type S3ConnectionForm,
  type S3ConnectionKind,
} from './s3Connections'
import { githubConnectionOf } from './githubConnection'
import type { TeamConfigSectionProps } from './sections'
import { TeamGithubConnection } from './TeamGithubConnection'
import { TextField } from './TextField'

export function TeamConnectionsSection({
  section,
  team,
}: TeamConfigSectionProps): React.JSX.Element {
  const query = useListTeamConnectionsApiInternalTeamsTeamIdConnectionsGet(team.id)
  const assigned = query.data?.status === 200 ? query.data.data.connections : undefined
  return (
    <div>
      <SettingsPageHeader section={section} />
      {query.isLoading ? (
        <SettingsBlock>
          <Skeleton className="h-24 w-full" />
        </SettingsBlock>
      ) : query.isError || assigned === undefined ? (
        <SettingsBlock>
          <p className="text-sm text-destructive" role="alert">
            {getApiErrorDetail(query.error) ?? 'The connections failed to load.'}
          </p>
        </SettingsBlock>
      ) : (
        <>
          <TeamGithubConnection team={team} connection={githubConnectionOf(assigned)} />
          <SettingsSection
            title="S3 connections"
            description="The team picks from these when it submits a batch or adds a benchmark from its own bucket."
          >
            <AssignedConnections team={team} connections={s3ConnectionsOf(assigned)} />
          </SettingsSection>
          <SettingsSection
            title="Assign an S3 connection"
            description="An assumed role stores no secret, so offer it first. Fall back to a key pair when the customer cannot create a role."
          >
            <AssignConnectionForm
              team={team}
              takenNames={assigned.map((connection) => connection.display_name)}
            />
          </SettingsSection>
        </>
      )}
    </div>
  )
}

/**
 * Both reads of a team's connections: the staff list on this page and the
 * customer-facing list behind New batch and the benchmark dialog. The
 * query client holds responses for five minutes (main.tsx), so without
 * the second one a staff member who assigns a connection and walks to the
 * team's pipeline page sees the cached empty answer until a reload.
 */
function useInvalidateConnections(teamId: string): () => void {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({
      queryKey: getListTeamConnectionsApiInternalTeamsTeamIdConnectionsGetQueryKey(teamId),
    })
    void queryClient.invalidateQueries({ queryKey: getListConnectionsApiConnectionsGetQueryKey() })
  }
}

function AssignedConnections({
  team,
  connections,
}: {
  team: ImpersonatableTeam
  connections: ConnectionAdminItem[]
}): React.JSX.Element {
  const invalidate = useInvalidateConnections(team.id)
  // The row being removed. The mutation lives here, above the dialog, so
  // its answer lands even if the dialog unmounts (ConfirmDialog).
  const [removing, setRemoving] = useState<ConnectionAdminItem | null>(null)
  const [removeError, setRemoveError] = useState<string | null>(null)
  const remove = useDeleteTeamConnectionApiInternalConnectionsConnectionIdDelete({
    mutation: {
      onSuccess: (response) => {
        invalidate()
        setRemoving(null)
        if (response.status === 200) toast.success(`${response.data.display_name} removed`)
      },
      onError: (error: unknown) => {
        setRemoveError(getApiErrorDetail(error) ?? 'The connection was not removed')
      },
    },
  })

  if (connections.length === 0) {
    return <p className="text-sm text-muted-foreground">No S3 connection is assigned yet.</p>
  }
  return (
    <>
      <SettingsList>
        {connections.map((connection) => (
          <ConnectionRow
            key={connection.id}
            connection={connection}
            onRemove={() => {
              setRemoveError(null)
              setRemoving(connection)
            }}
          />
        ))}
      </SettingsList>
      {removing !== null && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setRemoving(null)
          }}
          title="Remove connection"
          description={
            <>
              Remove <strong>{removing.display_name}</strong>? The team can no longer submit batches
              or benchmarks through it, and a nightly benchmark that reads through it fails.
            </>
          }
          confirmLabel="Remove"
          pendingLabel="Removing..."
          destructive
          isPending={remove.isPending}
          error={removeError}
          onConfirm={() => {
            remove.mutate({ connectionId: removing.id })
          }}
        />
      )}
    </>
  )
}

function ConnectionRow({
  connection,
  onRemove,
}: {
  connection: ConnectionAdminItem
  onRemove: () => void
}): React.JSX.Element {
  const kind: S3ConnectionKind = connection.kind === 's3_role' ? 's3_role' : 's3'
  const region = connection.region === null ? '' : ` · ${connection.region}`
  return (
    <SettingsListRow className="items-start py-3">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground">{connection.display_name}</span>
          <Badge variant="outline">{S3_CONNECTION_KIND_LABELS[kind]}</Badge>
        </div>
        <p className="break-all font-mono text-xs text-muted-foreground">
          {connection.identifier}
          {region}
        </p>
        {connection.external_id != null && (
          <CopyableValue label="External ID" value={connection.external_id} />
        )}
        {/* A key pair row inserted by hand from the api pod can lack its principal. */}
        {connection.principal !== '' && (
          <CopyableValue
            label={kind === 's3_role' ? 'TrainLoop principal' : 'Customer principal'}
            value={connection.principal}
          />
        )}
      </div>
      <RowAction
        label={`Remove ${connection.display_name}`}
        icon={Trash2}
        destructive
        onClick={onRemove}
      />
    </SettingsListRow>
  )
}

/** One value staff hand to the customer: its label, the value, a copy button. */
function CopyableValue({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <span className={`w-36 shrink-0 ${EYEBROW}`}>{label}</span>
      <code className="min-w-0 truncate font-mono text-xs text-foreground">{value}</code>
      <CopyButton value={value} label={`Copy ${label}`} className="shrink-0" />
    </div>
  )
}

function AssignConnectionForm({
  team,
  takenNames,
}: {
  team: ImpersonatableTeam
  takenNames: string[]
}): React.JSX.Element {
  const invalidate = useInvalidateConnections(team.id)
  const [form, setForm] = useState<S3ConnectionForm>(() => emptyS3ConnectionForm(team))
  // The API's answer to the last attempt. Any edit retires it.
  const [saveError, setSaveError] = useState<string | null>(null)
  const create = useCreateTeamConnectionApiInternalTeamsTeamIdConnectionsPost({
    mutation: {
      onSuccess: () => {
        invalidate()
        setForm(emptyS3ConnectionForm(team))
        toast.success('Connection assigned')
      },
      onError: (error: unknown) => {
        setSaveError(getApiErrorDetail(error) ?? 'The connection was not assigned')
      },
    },
  })

  const dirty = JSON.stringify(form) !== JSON.stringify(emptyS3ConnectionForm(team))
  const validationError = dirty ? validateS3Connection(form, takenNames) : null
  const canSubmit = !create.isPending && dirty && validationError === null

  const update = (patch: Partial<S3ConnectionForm>): void => {
    setForm((previous) => ({ ...previous, ...patch }))
    setSaveError(null)
  }
  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault()
    if (!canSubmit) return
    setSaveError(null)
    create.mutate({ teamId: team.id, data: s3ConnectionRequest(form) })
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-xl space-y-4">
      <KindPicker
        value={form.kind}
        onPick={(kind) => {
          update({ kind })
        }}
      />
      <TextField
        id="s3-connection-name"
        label="Name"
        value={form.display_name}
        onChange={(display_name) => {
          update({ display_name })
        }}
        placeholder="Acme S3"
        hint="The team sees this name when it picks a connection."
      />
      {form.kind === 's3_role' ? (
        <RoleFields form={form} onChange={update} />
      ) : (
        <KeyPairFields form={form} onChange={update} />
      )}
      <TextField
        id="s3-connection-region"
        label="Region"
        value={form.region}
        onChange={(region) => {
          update({ region })
        }}
        placeholder="us-west-2"
        hint="Requests are signed for the region the bucket lives in."
        mono
      />
      <SettingsFormFooter dirty={dirty} error={validationError ?? saveError}>
        <Button
          type="button"
          variant="outline"
          disabled={!dirty || create.isPending}
          onClick={() => {
            setForm(emptyS3ConnectionForm(team))
            setSaveError(null)
          }}
        >
          Discard
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          {create.isPending ? 'Assigning...' : 'Assign'}
        </Button>
      </SettingsFormFooter>
    </form>
  )
}

function KindPicker({
  value,
  onPick,
}: {
  value: S3ConnectionKind
  onPick: (kind: S3ConnectionKind) => void
}): React.JSX.Element {
  return (
    <div className="space-y-2">
      <Label htmlFor="s3-connection-kind">Kind</Label>
      <Select
        value={value}
        onValueChange={(next) => {
          // Radix only emits the values of the items below.
          if (isS3ConnectionKind(next)) onPick(next)
        }}
      >
        <SelectTrigger id="s3-connection-kind">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {S3_CONNECTION_KINDS.map((kind) => (
            <SelectItem key={kind} value={kind}>
              {S3_CONNECTION_KIND_LABELS[kind]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

interface KindFieldsProps {
  form: S3ConnectionForm
  onChange: (patch: Partial<S3ConnectionForm>) => void
}

function RoleFields({ form, onChange }: KindFieldsProps): React.JSX.Element {
  return (
    <TextField
      id="s3-connection-role-arn"
      label="Role ARN"
      value={form.role_arn}
      onChange={(role_arn) => {
        onChange({ role_arn })
      }}
      placeholder="arn:aws:iam::123456789012:role/trainloop-acme"
      hint="The customer creates this role in their account, and its name must start with trainloop-. After you assign it, its row above shows the External ID and the principal the role's trust policy needs."
      mono
    />
  )
}

function KeyPairFields({ form, onChange }: KindFieldsProps): React.JSX.Element {
  return (
    <>
      <TextField
        id="s3-connection-access-key-id"
        label="Access key id"
        value={form.access_key_id}
        onChange={(access_key_id) => {
          onChange({ access_key_id })
        }}
        placeholder="AKIAIOSFODNN7EXAMPLE"
        mono
      />
      <TextField
        id="s3-connection-secret-access-key"
        label="Secret access key"
        type="password"
        value={form.secret_access_key}
        onChange={(secret_access_key) => {
          onChange({ secret_access_key })
        }}
        hint="The secret is encrypted before the connection is written and never shown again."
        mono
      />
      <TextField
        id="s3-connection-principal-arn"
        label="Principal ARN"
        value={form.principal_arn}
        onChange={(principal_arn) => {
          onChange({ principal_arn })
        }}
        placeholder="arn:aws:iam::123456789012:user/trainloop-reader"
        hint="Enter the IAM user or role the key pair belongs to. The team sees it so they know which principal to grant on their bucket."
        mono
      />
    </>
  )
}
